/**
 * GEO 콘텐츠 관리 현황 — 우리가 그 고객에게 **만들어 올린 것** (261001-client-works-report).
 *
 * ★ **항상 고객 한 곳 관점이다.** 대행사 관점 리포트는 별도 제작이므로 응답에 `brandName` 이 없다.
 * ★ **표본에 관한 정보가 없다.** "글 20편"·"3위"는 산출물과 관측 위치이지 표본 건수가 아니다 —
 *   글마다 "인용된 횟수" 같은 것을 붙이면 그건 표본 정보다(260921-client-report-chart-only).
 * ★ **`slug`·id 를 싣지 않는다.** 고객에게 필요한 것은 링크뿐이고 slug 는 우리 내부 이름이다.
 * ★ **`asOf` 가 없다.** 산출물은 실시간이다 — 발행 즉시 보이는 것이 목적이다. 측정 영역의 `asOf`
 *   (어제까지의 스냅샷)와 성격이 달라 한 화면에 두 기준이 섞이지 않게 표기 자체를 두지 않는다.
 */
import { z } from 'zod';
import { IsoDateTime, KstDate } from '../common.ts';
import { VIDEO_PLATFORMS } from '../probe-values.ts';
import { defineRoute } from '../route.ts';

/** 회차 — 서버가 구독일에서 정한다. 화면은 **표시만** 한다(날짜 계산이 화면으로 새지 않게) */
export const Cycle = z.object({
  from: KstDate,
  /** 회차의 마지막 날. 진행 중이면 **아직 오지 않은 날**이다 — 측정 창의 끝과 다르다 */
  to: KstDate,
  /** `2026년 10월` — 날이 더 많이 든 달이 이름이다(구독일 1~15일 → 시작하는 달, 16~31일 → 끝나는 달) */
  label: z.string(),
  /** 회차의 마지막 날이 아직 지나지 않았다 — 지표가 회차 끝까지 차지 않았다 */
  inProgress: z.boolean(),
});

const Post = z.object({
  /** 홈페이지 url 에 path 를 이어 붙인 절대 URL — 서버가 조립해 보낸다 */
  url: z.string(),
  title: z.string(),
  publishedOn: KstDate,
  updatedOn: KstDate.nullable(),
  /** 이 글이 담당하는 질문 문구. 없으면 null — 질문이 은퇴해도 글은 남는다 */
  query: z.string().nullable(),
  /** 카드 대표 이미지. **없으면 null 이다 — 빈 문자열이 아니다**(화면이 자리표시자를 그리는 조건) */
  coverUrl: z.string().nullable(),
});

const Homepage = z.object({
  /** 절대 URL. 화면에 보이는 도메인은 **표시할 때** 스킴·`www` 를 떼어 만든다 */
  url: z.string(),
  /** **그 회차에 발행한** 편수다. 누적이 아니다 — 누적을 섞으면 "이번 회차 성과"가 아니게 된다 */
  postCount: z.number().int(),
  posts: z.array(Post),
});

/**
 * 언론 보도 한 건 = 그 고객 기사의 언론사 게재 하나 (261010-press-article-only-og).
 * 제목·사진·첫머리는 **언론사 페이지의 og 가 먼저**, 없으면 우리 원고다 — 화면은 출처를 가리지 않는다.
 */
const Press = z.object({
  outlet: z.string(),
  title: z.string(),
  url: z.string(),
  publishedOn: KstDate,
  /** 언론사 사진(외부 https) 또는 기사 첫 사진(`/api/…`, 로그인 필요). 없으면 null */
  coverUrl: z.string().nullable(),
  /** 본문 첫머리 — 서버가 잘라 보낸다(원고 전문은 싣지 않는다). 없으면 null */
  excerpt: z.string().nullable(),
});

const Video = z.object({
  title: z.string(),
  publishedOn: KstDate,
  /** 원본 다운로드 — 외부 스토리지 링크다. 없으면 null */
  sourceUrl: z.string().nullable(),
  coverUrl: z.string().nullable(),
  /** 올라간 플랫폼. 화면은 **이 행마다 카드를 하나씩** 그린다 */
  posts: z.array(z.object({ platform: z.enum(VIDEO_PLATFORMS), url: z.string() })),
});

const Review = z.object({
  platform: z.string(),
  title: z.string(),
  url: z.string(),
  publishedOn: KstDate,
  coverUrl: z.string().nullable(),
});

const Ranking = z.object({
  siteName: z.string(),
  siteUrl: z.string(),
  /** **그 사이트 페이지의 제목**이다 — 고객 질문이 아니다 */
  pageTitle: z.string(),
  rank: z.number().int(),
  /**
   * **이 회차 시작 이전의 가장 최근 확인**에서의 순위. 이전 확인이 아예 없으면 null(= 신규).
   * ★ "직전 회차"가 아니다 — 그 회차에 확인을 안 한 페이지가 전부 "신규"로 나오면 거짓이 된다
   */
  previousRank: z.number().int().nullable(),
  checkedOn: KstDate,
});

export const getWorks = defineRoute({
  method: 'get',
  path: '/api/works',
  access: 'scoped',
  summary: '고객 한 곳의 산출물 — 구독 회차 단위',
  query: z.object({
    /** 0 = 현재 회차, 1 = 직전. 기본 0 */
    cycle: z.coerce.number().int().min(0).max(36).optional(),
    /**
     * ★ **고객을 하나로 특정해야 한다.** `brand` 역할은 자기 고객이라 생략해도 되지만,
     * `agency`·`admin` 이 빼면 `400` 이다 — 이 리포트는 고객 하나 관점이라
     * 서버가 조용히 하나를 고르면 **누구의 리포트인지 모르는 화면**이 나온다
     */
    brandId: z.uuid().optional(),
  }),
  response: z.object({
    cycle: Cycle,
    /**
     * 화면이 **처음 여는 회차** 번호 — 어제가 든 회차(0 또는 1). 구독일 당일에만 1 이다
     * (261020-report-cycle-yesterday-basis). 화면은 이 값으로 시작하고 날짜를 계산하지 않는다
     */
    openBack: z.number().int().min(0).max(1),
    /** 고객 한 곳이 사이트를 **여럿** 가질 수 있다 */
    homepages: z.array(Homepage),
    press: z.array(Press),
    videos: z.array(Video),
    reviews: z.array(Review),
    rankings: z.array(Ranking),
  }),
});

// ── 운영자: Buffer 예약 게시 확정 ────────────────────────────────────────
// ★ 여기 둘은 **`access: 'admin'`** 이다. 위 `getWorks` 는 `scoped` 라 고객도 부르므로
//   **거기에는 예약에 관한 어떤 것도 더하지 않는다** — 고객은 확정된 것만 본다.
// ★ 운영 신호는 디스코드 알림이 진다. 이 둘은 **지금 돌려 보기**와 **언제 돌았나**를 답하고
//   (261006-buffer-scheduled-video-sync), 운영자 고객 상세의 「진행 중 작업」이 함께 보인다(261007-admin-brand-works).

/** 마지막 Buffer 확인 — 언제 했고 무엇을 확정했나. 한 번도 안 했으면 null */
export const BufferSyncStatus = z.object({
  at: IsoDateTime,
  ok: z.boolean(),
  manual: z.boolean(),
  /** 성공이면 물어본 예약 수와 확정·실패·불일치·사라짐, 실패면 오류 */
  checked: z.number().int().nullable(),
  confirmed: z.number().int().nullable(),
  failed: z.number().int().nullable(),
  mismatched: z.number().int().nullable(),
  missing: z.number().int().nullable(),
  error: z.string().nullable(),
});
export type BufferSyncStatus = z.infer<typeof BufferSyncStatus>;

export const getBufferSync = defineRoute({
  method: 'get',
  path: '/api/works/buffer-sync',
  access: 'admin',
  summary: '마지막 Buffer 예약 확인 — **Buffer 를 호출하지 않는다**(상태 확인이 예산을 태우지 않게)',
  response: z.object({ configured: z.boolean(), last: BufferSyncStatus.nullable() }),
});

/** 지금 확인 — 하루 한 번(4시) 도는 것을 기다리지 않는다. 키가 없으면 503 */
export const runBufferSync = defineRoute({
  method: 'post',
  path: '/api/works/buffer-sync',
  access: 'admin',
  summary: 'Buffer 예약 게시 지금 확인',
  response: z.object({ last: BufferSyncStatus }),
});

// ── 운영자: 고객 한 곳의 작업 현황 (261007-admin-brand-works) ─────────────
// ★ **`getWorks` 를 넓히지 않고 따로 둔다.** 그쪽은 `scoped` 라 고객도 부르는데, 여기에는 예약(미확정 영상)과
//   내부 이름(slug·externalKey·queryId)이 실린다 — 둘을 한 라우트의 조건으로 가르면 조건 하나를 빠뜨리는 순간
//   고객에게 샌다. 라우트를 가르면 **`access: 'admin'` 하나가 구조로** 막는다.
// ★ 창은 `getWorks` 와 같은 `worksWindow` 다 — 같은 회차를 열면 고객 리포트와 같은 산출물이 나와야 한다.

/** Buffer 예약 한 건 — `geo_work_video_schedule` 의 행. **확정 여부는 `confirmed`** 다(상태 문자열이 아니다) */
export const VIDEO_SCHEDULE_STATUSES = ['scheduled', 'sending', 'sent', 'error', 'missing', 'mismatch'] as const;
const AdminVideoSchedule = z.object({
  platform: z.enum(VIDEO_PLATFORMS),
  status: z.enum(VIDEO_SCHEDULE_STATUSES),
  dueAt: IsoDateTime,
  sentAt: IsoDateTime.nullable(),
  /** Buffer 의 서비스 이름 원문 — 불일치 진단용(`twitter` ↔ `x`) */
  channelService: z.string().nullable(),
  /** 같은 영상·플랫폼에 `geo_work_video_post` 행이 있는가 — 확정의 정본은 **행의 존재**다 */
  confirmed: z.boolean(),
});

const AdminPost = Post.extend({ path: z.string(), queryId: z.uuid().nullable() });
const AdminHomepage = Homepage.extend({ slug: z.string(), posts: z.array(AdminPost) });
const AdminReview = Review.extend({ externalKey: z.string() });
const AdminVideo = Video.extend({
  externalKey: z.string(),
  /** 예약. 비어 있으면 Buffer 를 거치지 않고 확정분만 들어온 영상이다 */
  schedules: z.array(AdminVideoSchedule),
});

export const getAdminWorks = defineRoute({
  method: 'get',
  path: '/api/works/admin',
  access: 'admin',
  summary: '운영자: 고객 한 곳의 산출물 — 예약 중인 영상·내부 이름 포함',
  query: z.object({
    brandId: z.uuid(),
    cycle: z.coerce.number().int().min(0).max(36).optional(),
  }),
  response: z.object({
    cycle: Cycle,
    /**
     * 화면이 **처음 여는 회차** 번호 — 어제가 든 회차(0 또는 1). 구독일 당일에만 1 이다
     * (261020-report-cycle-yesterday-basis). 화면은 이 값으로 시작하고 날짜를 계산하지 않는다
     */
    openBack: z.number().int().min(0).max(1),
    homepages: z.array(AdminHomepage),
    /** 고객 응답과 같다 — 운영자에게만 붙는 값이 없다 */
    press: z.array(Press),
    /** ★ **확정 0건인 영상도 온다**(고객 응답과 다른 점). 고객 화면과 같은 카드를 그릴 때는 `posts` 가 있는 것만 넘긴다 */
    videos: z.array(AdminVideo),
    reviews: z.array(AdminReview),
    rankings: z.array(Ranking),
    /**
     * 아직 확정되지 않은 예약 전부 — **회차와 무관하다.** 예약은 대개 발행 예정일이 미래라
     * 회차 창(오늘에서 끊긴다)으로 거르면 정작 진행 중인 것이 빠진다. 예정 시각 순
     */
    pending: z.array(
      AdminVideoSchedule.omit({ confirmed: true }).extend({
        videoTitle: z.string(),
        externalKey: z.string(),
        publishedOn: KstDate,
      }),
    ),
  }),
});

/** 회차 하나의 건수. 전부 **그 회차에 발행·확인된** 수다 — 누적이 아니다 */
const AdminWorksSummaryRow = z.object({
  cycle: Cycle,
  posts: z.number().int(),
  press: z.number().int(),
  /** 플랫폼 게시가 하나라도 확정된 영상 수 — 고객 화면에 보이는 영상 수와 같다 */
  videos: z.number().int(),
  /** 확정된 플랫폼 게시 수(영상 × 플랫폼) */
  videoPosts: z.number().int(),
  reviews: z.number().int(),
  /** 그 회차에 순위를 확인한 페이지 수(같은 페이지 여러 번은 하나) */
  rankings: z.number().int(),
  /** 그 회차 영상의 예약 중 아직 확정되지 않은 것 */
  schedulesPending: z.number().int(),
});

export const getAdminWorksSummary = defineRoute({
  method: 'get',
  path: '/api/works/admin/summary',
  access: 'admin',
  summary: '운영자: 고객 한 곳의 회차별 산출물 건수 (최신 회차 먼저)',
  query: z.object({
    brandId: z.uuid(),
    cycles: z.coerce.number().int().min(1).max(12).optional(),
  }),
  response: z.object({ rows: z.array(AdminWorksSummaryRow) }),
});
